import React, {useEffect, useState} from 'react';
import {AnimatePresence, motion} from 'framer-motion';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import {useAppContext} from '../../context/AppContext';
import {Card, CardContent} from '../../components/ui/card';
import {Button} from '../../components/ui/button';
import {Spinner} from '../../components/Spinner';
import {
	FaDownload,
	FaEdit, FaExchangeAlt,
	FaHistory, FaPaintBrush, FaPalette,
	FaSearch,
	FaSortAmountDown,
	FaSortAmountUp,
	FaTrash
} from 'react-icons/fa';
import apiService from '../../services/api';

let useToast;
try {
	useToast = require('../../contexts/ToastContext').useToast;
} catch (e) {

}

const fadeIn = {
	hidden: {opacity: 0, y: 20},
	visible: {opacity: 1, y: 0}
};

const ImageHistory = () => {
	const {t} = useTranslation();
	const {setIsLoading: setGlobalLoading} = useAppContext();
	const {addToast} = useToast();
	const navigate = useNavigate();
	const [images, setImages] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState(null);
	const [sortDirection, setSortDirection] = useState('desc');
	const [searchQuery, setSearchQuery] = useState('');
	const [selectedImage, setSelectedImage] = useState(null);
	const [page, setPage] = useState(1);
	const [hasMore, setHasMore] = useState(true);
	const [totalStorage, setTotalStorage] = useState(0);
	const limit = 20;
	const [showOriginal, setShowOriginal] = useState(false);
	
	// Load images on component mount
	useEffect(() => {
		loadImages(true, sortDirection); // Pass true to reset images when filter/sort changes
	}, [sortDirection]);
	
	// Effect to handle pagination - only load more when page changes
	useEffect(() => {
		// Only load more images when page changes and page > 1
		// This prevents duplicate loads on initial render
		if (page > 1) {
			loadImages(false);
		}
	}, [page]);
	
	// Load images from the API
	const loadImages = async (reset = false, sortDirection = 'desc', sortField = 'created_at') => {
		try {
			setIsLoading(true);
			setGlobalLoading(true);
			
			const offset = reset ? 0 : (page - 1) * limit;
			const type = null;
			
			const response = await apiService.getUserImageHistory(
				limit,
				offset,
				type,
				sortField,
				sortDirection
			);
			
			const newImages = response.files || [];
			
			// Set total storage from server response
			setTotalStorage(response.total || 0);
			
			if (reset) {
				setImages(newImages);
				setPage(1);
			} else {
				setImages(prev => [...prev, ...newImages]);
			}
			
			// Check if there are more images to load
			setHasMore(newImages.length === limit);
			
			setError(null);
		} catch (error) {
			console.error('Error loading images:', error);
			setError(error.message || t('errors.loadingFailed'));
			addToast({type: 'error', message: error.message || t('errors.loadingFailed')});
		} finally {
			setIsLoading(false);
			setGlobalLoading(false);
		}
	};	// Handle filter change
	
	// Handle sort direction change
	const handleSortChange = () => {
		setSortDirection(prev => prev === 'desc' ? 'asc' : 'desc');
		// The actual loading will be triggered by the useEffect
	};
	
	// Handle search
	const handleSearch = (e) => {
		e.preventDefault();
		// Filter images client-side for now
		// No need to call loadImages as we're just filtering the existing data
	};
	
	// Handle image selection
	const handleImageClick = (image) => {
		setSelectedImage(image);
	};
	
	// New function for image editing (from the new version)
	const handleEditImage = (image) => {
		try {
			navigate(`/photo-editor/${image.id}`);
		} catch (error) {
			console.error('Error navigating:', error);
			// Fallback for apps without routing
			window.alert(t('imageHistory.editNotAvailable') || 'Edit functionality is not available in this version.');
		}
	};
	
	// Handle image download (supports both property patterns)
	const handleDownload = (image) => {
		if (!image) return;
		
		try {
			const imageUrl = getImageUrl(image);
			const imageName = getImageName(image);
			
			const link = document.createElement('a');
			link.href = imageUrl;
			link.download = imageName;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			
			// Show toast notification if available
			addToast?.({type: 'success', message: t('imageHistory.downloadSuccess') || 'Download successful'});
		} catch (error) {
			console.error('Error downloading image:', error);
			addToast?.({type: 'error', message: t('errors.downloadFailed') || 'Download failed'});
		}
	};
	
	// Handle image deletion (supports both property patterns)
	const handleDelete = async (imageId) => {
		if (!window.confirm(t('common.confirmDelete') || 'Are you sure you want to delete this image?')) return;
		
		try {
			setIsLoading(true);
			setGlobalLoading(true);
			await apiService.deleteUserFile(imageId);
			
			// Remove the deleted image from the state
			setImages(prev => prev.filter(img => img.id !== imageId));
			
			// Close the detail view if the deleted image was selected
			if (selectedImage && selectedImage.id === imageId) {
				setSelectedImage(null);
			}
			
			// Show toast notification if available
			addToast?.({type: 'success', message: t('imageHistory.deleteSuccess') || 'Image deleted successfully'});
		} catch (error) {
			console.error('Error deleting image:', error);
			addToast?.({type: 'error', message: t('errors.deleteFailed') || 'Failed to delete image'});
		} finally {
			setIsLoading(false);
			setGlobalLoading(false);
		}
	};
	
	// Legacy function aliases for backward compatibility
	const handleDownloadImage = handleDownload;
	const handleDeleteImage = (image) => handleDelete(image.id);
	
	// Handle load more
	const handleLoadMore = () => {
		if (hasMore && !isLoading) {
			setPage(prev => prev + 1);
			// The loading will be triggered by the useEffect
		}
	};
	
	// Helper function to get image URL
	const getImageUrl = (image) => {
		if (!image) return '';
		
		// Handle different image object structures
		if (image.url) {
			return image.url;
		} else if (image.generatedImage && image.generatedImage.url) {
			return image.generatedImage.url;
		}
		
		return '';
	};
	
	// Helper function to get image name
	const getImageName = (image) => {
		if (!image) return 'image';
		
		// Handle different image object structures
		if (image.name) {
			return image.name;
		} else if (image.generatedImage && image.generatedImage.name) {
			return image.generatedImage.name;
		}
		
		return 'image';
	};
	
	// Helper function to get image size
	const getImageSize = (image) => {
		if (!image) return 0;
		
		// Handle different image structures
		if (image.size) return image.size;
		
		// Handle different API response structures
		if (image.generatedImage && image.generatedImage.size) {
			return image.generatedImage.size;
		}
		
		return 0;
	};
	
	// Helper function to get image date
	const getImageDate = (image) => {
		if (!image) return new Date();
		
		// Handle different date fields
		if (image.createdAt) return image.createdAt;
		if (image.created_at) return image.created_at;
		if (image.date) return image.date;
		
		// Handle different API response structures
		if (image.generatedImage && image.generatedImage.createdAt) {
			return image.generatedImage.createdAt;
		}
		
		return new Date();
	};
	
	// Helper function to get image metadata
	const getImageMetadata = (image) => {
		if (!image) return {};
		
		// Handle different metadata structures
		if (image['generatedImage'] && image.generatedImage.metadata) return image['generatedImage'].metadata;
		
		// For search-replace operations
		if (image.type === 'search-replace') {
			return {
				searchObject: image.searchObject || image.searchPrompt || '',
				replaceObject: image.replaceObject || image.newObject || '',
				newObject: image.newObject || image.replaceObject || '',
				type: 'search-replace'
			};
		}
		// For search-recolor operations
		if (image.type === 'search-recolor') {
			return {
				recolorObject: image.recolorObject || image.searchPrompt || '',
				newColor: image.newColor || '',
				type: 'search-recolor'
			};
		}
		
		// For inpainting operations
		if (image.type === 'inpaint' || image.type === 'inpaint_result') {
			return {
				prompt: image.prompt || '',
				translatedPrompt: image.translatedPrompt || '',
				type: 'inpaint'
			};
		}
		
		return {};
	};
	
	// Helper function to get image type label
	const getImageTypeLabel = (type) => {
		switch (type) {
			case 'inpaint':
			case 'inpaint_result':
				return t('imageHistory.types.inpaint');
			case 'search-replace':
				return t('imageHistory.types.searchReplace');
			case 'search-recolor':
				return t('imageHistory.types.searchRecolor');
			case 'upload':
				return t('imageHistory.types.upload');
			default:
				return t('imageHistory.types.unknown');
		}
	};
	
	const getImageTypeIcon = (type) => {
		switch (type) {
			case 'inpaint':
			case 'inpaint_result':
				return <FaPaintBrush className="nav-icon" />
			case 'search-replace':
				return <FaExchangeAlt className="nav-icon" />
			case 'search-recolor':
				return <FaPalette className="nav-icon" />
		}
	};
	
	// Helper function to format file size
	const formatFileSize = (bytes) => {
		if (bytes === 0) return t('imageHistory.sizeBytes', '0 Bytes');
		
		const k = 1024;
		const sizes = [t('imageHistory.sizeBytes'), t('imageHistory.sizeKB'), t('imageHistory.sizeMB'), t('imageHistory.sizeGB')];
		
		let i = 0;
		while (bytes >= k && i < sizes.length - 1) {
			bytes /= k;
			i++;
		}
		
		return `${parseFloat(bytes.toFixed(2))} ${sizes[i]}`;
	};
	// Helper function to format date
	const formatDate = (date) => {
		if (!date) return '';
		
		try {
			const d = new Date(date);
			return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'});
		} catch (e) {
			return '';
		}
	};
	
	// Helper function to get the original image if available
	const getOriginalImage = (image) => {
		if (!image) return null;
		
		// If the image already has an originalImage property, use it
		if (image.originalImage) return image.originalImage;
		
		// For inpainting, the original image might be stored in a different field
		if (image.type === 'inpaint' || image.type === 'inpaint_result') {
			if (image.sourceImage) return image.sourceImage;
		}
		
		// For search-replace and search-recolor, the original image might be stored in a different field
		if (image.type === 'search-replace' || image.type === 'search-recolor') {
			if (image.sourceImage) return image.sourceImage;
		}
		
		// If no original image is available, return null
		return null;
	};
	
	// Function to check if an image has a valid original image
	const hasOriginalImage = (image) => {
		if (!image) return false;
		
		// Check if the image has an originalImage property
		if (image.originalImage && image.originalImage.url) {
			return true;
		}
		
		return false;
	};
	
	// Filter images by search query
	const filteredImages = searchQuery
		? images.filter(img => {
			const name = getImageName(img).toLowerCase();
			const metadata = getImageMetadata(img);
			const metadataString = JSON.stringify(metadata).toLowerCase();
			
			return name.includes(searchQuery.toLowerCase()) ||
				metadataString.includes(searchQuery.toLowerCase());
		})
		: images;
	
	// ImageCard component for displaying image with hover animation
	const ImageCard = ({image, onSelect, onDownload, onEdit}) => {
		const {t} = useTranslation();
		const [isHovering, setIsHovering] = useState(false);
		const [hoverInterval, setHoverInterval] = useState(null);
		const [showOriginalOnHover, setShowOriginalOnHover] = useState(false);
		
		// Get metadata from the image
		const metadata = getImageMetadata(image);
		
		// Check if the image has a valid original image
		const hasValidOriginalImage = hasOriginalImage(image);
		
		// Flag to track if we have an originalImageId but the image is missing
		const missingOriginalImage = image.generatedImage &&
			image.generatedImage.metadata &&
			image.generatedImage.metadata.originalImageId &&
			!hasValidOriginalImage;
		
		// Get the original image if available
		const originalImage = image.originalImage;
		
		// Handle mouse enter
		const handleMouseEnter = () => {
			setIsHovering(true);
			
			// For generation processes, set up an interval to toggle between original and generated
			// Only do this if we actually have a valid original image
			if (isGenerationProcess(image) && hasValidOriginalImage) {
				// Immediately show the original image when hovering starts
				setShowOriginalOnHover(true);
				
				// Set up an interval to toggle between original and generated
				const interval = setInterval(() => {
					setShowOriginalOnHover(prev => !prev);
				}, 1500); // Toggle every 1.5 seconds
				
				setHoverInterval(interval);
			}
		};
		
		// Handle mouse leave
		const handleMouseLeave = () => {
			setIsHovering(false);
			setShowOriginalOnHover(false);
			
			// Clear the interval when not hovering
			if (hoverInterval) {
				clearInterval(hoverInterval);
				setHoverInterval(null);
			}
		};
		
		// Clean up the interval when the component unmounts
		useEffect(() => {
			return () => {
				if (hoverInterval) {
					clearInterval(hoverInterval);
				}
			};
		}, [hoverInterval]);
		
		// Check if this is a generation process
		const isGenerationProcess = (img) => {
			if (!img) return false;
			return img.type === 'search-replace' || img.type === 'search-recolor' || img.type === 'inpaint' ||
				img.type === 'inpaint_result';
		};
		
		if (!image) return null;
		
		return (
			<motion.div
				whileHover={{scale: 1.03, y: -5}}
				className="cursor-pointer"
				onClick={onSelect}
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
			>
				<Card variant="glass" className="overflow-hidden h-full">
					<CardContent className="p-0 flex flex-col h-full">
						<div className="relative aspect-square overflow-hidden">
							<div className="relative w-full h-full">
								{/* Generated image */}
								<motion.div
									animate={{opacity: showOriginalOnHover && isHovering && hasValidOriginalImage ? 0 : 1}}
									transition={{duration: 0.3}}
									className="absolute inset-0"
								>
									<img
										src={getImageUrl(image)}
										alt={getImageName(image)}
										className="w-full h-full object-cover"
										loading="lazy"
									/>
								</motion.div>
								
								{/* Original image (if available) */}
								{hasValidOriginalImage && (
									<motion.div
										initial={{opacity: 0}}
										animate={{opacity: showOriginalOnHover && isHovering ? 1 : 0}}
										transition={{duration: 0.3}}
										className="absolute inset-0 z-[1]"
									>
										<img
											src={getImageUrl(originalImage)}
											alt="Original"
											className="w-full h-full object-cover"
											loading="lazy"
										/>
									</motion.div>
								)}
								
								{/* Hover effect - show metadata overlay when hovering */}
								{isGenerationProcess(image) && (
									<AnimatePresence>
										{isHovering && !showOriginalOnHover && (
											<motion.div
												initial={{opacity: 0}}
												animate={{opacity: 1}}
												exit={{opacity: 0}}
												transition={{duration: 0.3}}
												className="absolute inset-0 z-10 bg-background/10 backdrop-blur-[1px] flex items-center justify-center"
											>
												<div
													className="text-center p-3 text-white bg-background/80 backdrop-blur-sm rounded-lg">
													<div className="text-sm font-medium mb-1">
														{t('imageHistory.generatedUsing') || 'Generated using'}:
													</div>
													<div className="text-white font-bold">
														{image.type === 'search-replace' && (
															<>
																{metadata.searchObject} → {metadata.newObject || metadata.replaceObject}
															</>
														)}
														{image.type === 'search-recolor' && (
															<>
																{metadata.recolorObject} → {metadata.newColor}
															</>
														)}
														{(image.type === 'inpaint' || image.type === 'inpaint_result') && (
															<>
																{image.prompt || metadata.prompt || t('inpaint.inpainting') || 'Inpainting'}
															</>
														)}
													</div>
												</div>
											</motion.div>
										)}
									</AnimatePresence>
								)}
								
								{/* No original available indicator */}
								{isHovering && showOriginalOnHover && !hasValidOriginalImage && isGenerationProcess(image) && (
									<motion.div
										initial={{opacity: 0}}
										animate={{opacity: 1}}
										exit={{opacity: 0}}
										transition={{duration: 0.3}}
										className="absolute inset-0 z-10 bg-background/10 backdrop-blur-[1px] flex items-center justify-center"
									>
										<div
											className="text-center p-3 bg-background/80 backdrop-blur-sm rounded-lg max-w-[80%]">
											<div className="text-sm font-medium mb-1">
												{missingOriginalImage
													? (t('imageHistory.originalNotFound') || 'Original image not found')
													: (t('imageHistory.noOriginalAvailable') || 'Original not available')}
											</div>
											<div className="text-xs text-text-light mt-1">
												{t('imageHistory.originalImageInfo') || 'Original images are stored when available'}
											</div>
										</div>
									</motion.div>
								)}
								
								{/* Original image indicator */}
								{isHovering && showOriginalOnHover && hasValidOriginalImage && (
									<div
										className="absolute top-2 left-2 bg-background/80 backdrop-blur-sm text-xs px-2 py-1 rounded-full">
										{t('imageHistory.original') || 'Original'}
									</div>
								)}
								
								{/* Indicator for image type */}
								<div
									className="absolute top-2 right-2 text-white bg-background/80 backdrop-blur-sm text-xs px-2 py-1 rounded-full">
									{getImageTypeLabel(
										getImageMetadata(image).type || image.type
									)}
								</div>
								
								{/* Hover indicator */}
								{isGenerationProcess(image) && (
									<div
										className="absolute bottom-2 left-2 bg-background/80 text-white backdrop-blur-sm text-xs px-2 py-1 rounded-full">
										{isHovering ?
											(showOriginalOnHover ?
												(hasValidOriginalImage ?
													(t('imageHistory.showingOriginal') || 'Showing original') :
													(t('imageHistory.noOriginal') || 'No original available')) :
												(t('imageHistory.showingGenerated') || 'Showing generated')) :
											(hasValidOriginalImage ?
												(t('imageHistory.hoverToCompare') || 'Hover to compare') :
												(t('imageHistory.hoverForInfo') || 'Hover for info'))
										}
									</div>
								)}
							</div>
						</div>
						<div className="p-3 flex-grow">
							<div className="truncate text-sm font-medium">
								{getImageName(image)}
							</div>
							<div className="flex justify-between items-center mt-2 text-xs text-text-light">
								<div>{formatFileSize(getImageSize(image))}</div>
								<div>{formatDate(getImageDate(image))}</div>
							</div>
						</div>
					</CardContent>
				</Card>
			</motion.div>
		);
	};
	
	// Render the detailed view of a selected image
	const renderImageDetails = () => {
		if (!selectedImage) return null;
		
		// Get metadata for the selected image
		const metadata = getImageMetadata(selectedImage);
		
		return (
			<div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
				<motion.div
					initial={{opacity: 0, scale: 0.9}}
					animate={{opacity: 1, scale: 1}}
					exit={{opacity: 0, scale: 0.9}}
					className="bg-background rounded-xl shadow-xl overflow-hidden max-w-4xl w-full max-h-[90vh] flex flex-col"
				>
					<div className="flex justify-between items-center p-4 border-b border-border">
						<h3 className="text-xl font-medium">{getImageName(selectedImage)}</h3>
						<button
							onClick={() => setSelectedImage(null)}
							className="text-text-light hover:text-primary transition-colors"
						>
							<svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24"
								 stroke="currentColor">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
									  d="M6 18L18 6M6 6l12 12"/>
							</svg>
						</button>
					</div>
					
					<div className="flex-grow overflow-y-auto p-4">
						<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
							{/* Image preview section */}
							<div className="space-y-4">
								<div className="aspect-square rounded-lg overflow-hidden border border-border relative">
									{/* Generated image */}
									<img
										src={getImageUrl(selectedImage)}
										alt={getImageName(selectedImage)}
										className={`w-full h-full object-contain ${showOriginal ? 'opacity-0' : 'opacity-100'}`}
										style={{
											transition: 'opacity 0.3s ease-in-out',
											position: showOriginal ? 'absolute' : 'relative'
										}}
									/>
									
									{/* Original image (if available) */}
									{selectedImage.originalImage && (
										<img
											src={getImageUrl(selectedImage.originalImage)}
											alt="Original"
											className={`w-full h-full object-contain ${showOriginal ? 'opacity-100' : 'opacity-0'}`}
											style={{
												transition: 'opacity 0.3s ease-in-out',
												position: 'absolute',
												top: 0,
												left: 0,
												display: showOriginal ? 'block' : 'none'
											}}
										/>
									)}
									
									{/* Image type indicator */}
									<div
										className="absolute top-2 right-2 bg-background/80 backdrop-blur-sm text-xs px-2 py-1 rounded-full">
										{getImageTypeIcon(selectedImage.type)}
										{getImageTypeLabel(selectedImage.type)}
									</div>
								</div>
								
								{/* Toggle button for original/generated */}
								<div className="flex justify-center">
									<Button
										onClick={() => setShowOriginal(!showOriginal)}
										variant="outline"
										className="gap-2"
									>
										{showOriginal ? (
											<>
												<FaHistory/>
												{t('imageHistory.showGenerated') || 'Show Generated'}
											</>
										) : (
											<>
												<FaHistory/>
												{t('imageHistory.showOriginal') || 'Show Original'}
											</>
										)}
									</Button>
								</div>
							</div>
							
							{/* Image details section */}
							<div>
								<div className="space-y-4">
									<div>
										<h4 className="text-lg font-medium text-primary mb-2">
											{t('imageHistory.imageDetails')}
										</h4>
									</div>
									
									<div className="space-y-4">
										<div className="grid grid-cols-2 gap-2">
											<div className="text-text-light">{t('common.type')}:</div>
											<div>
												{getImageTypeLabel(selectedImage.type)}
											</div>
											
											<div className="text-text-light">{t('common.size')}:</div>
											<div>{formatFileSize(getImageSize(selectedImage))}</div>
											
											<div className="text-text-light">{t('common.created')}:</div>
											<div>{formatDate(getImageDate(selectedImage))}</div>
										</div>
										
										{/* Metadata section */}
										<div className="mt-4">
											<h5 className="font-medium text-primary mb-2">{t('common.metadata')}</h5>
											<div className="bg-background/30 backdrop-blur-sm p-4 rounded-lg">
												{/* Prompt information */}
												{selectedImage.prompt && (
													<div className="mb-4 pb-4 border-b border-background/20">
														<div
															className="font-medium text-primary mb-2">{t('inpaint.prompt') || 'Prompt'}</div>
														<div className="text-sm">{selectedImage.prompt}</div>
													</div>
												)}
												
												{/* Type-specific metadata */}
												{selectedImage.type === 'search-replace' && (
													<div className="grid grid-cols-2 gap-2">
														<div
															className="text-text-light">{t('searchReplace.whatToFind')}:
														</div>
														<div>{metadata.searchObject}</div>
														
														<div
															className="text-text-light">{t('searchReplace.replaceWith')}:
														</div>
														<div>{metadata.newObject || metadata.replaceObject}</div>
													</div>
												)}
												
												{selectedImage.type === 'search-recolor' && (
													<div className="grid grid-cols-2 gap-2">
														<div
															className="text-text-light">{t('searchRecolor.whatToFind')}:
														</div>
														<div>{metadata.recolorObject}</div>
														
														<div
															className="text-text-light">{t('searchRecolor.newColor')}:
														</div>
														<div>{metadata.newColor}</div>
													</div>
												)}
												
												{(selectedImage.type === 'inpaint' || selectedImage.type === 'inpaint_result') && selectedImage.prompt && (
													<div className="grid grid-cols-2 gap-2">
														<div className="text-text-light">{t('inpaint.prompt')}:</div>
														<div>{selectedImage.prompt}</div>
													</div>
												)}
												
												{/* Additional metadata if available */}
												{metadata.translatedPrompt && (
													<div className="mt-4 pt-4 border-t border-background/20">
														<div className="grid grid-cols-2 gap-2">
															<div
																className="text-text-light">{t('common.translatedPrompt') || 'Translated Prompt'}:
															</div>
															<div>{metadata.translatedPrompt}</div>
														</div>
													</div>
												)}
												
												{/* Mask image if available */}
												{selectedImage.maskImage && (
													<div className="mt-4 pt-4 border-t border-background/20">
														<div
															className="font-medium text-primary mb-2">{t('inpaint.mask') || 'Mask'}</div>
														<div className="flex items-center">
															<div className="w-16 h-16 mr-2 overflow-hidden rounded">
																<img
																	src={getImageUrl(selectedImage.maskImage)}
																	alt="Mask"
																	className="w-full h-full object-cover"
																/>
															</div>
															<span>{getImageName(selectedImage.maskImage)}</span>
														</div>
													</div>
												)}
											</div>
										</div>
										
										<div className="flex flex-wrap gap-3 mt-8">
											{/* Edit button */}
											<motion.div key="edit-button" whileHover={{scale: 1.05}}
														whileTap={{scale: 0.95}}>
												<Button
													onClick={() => handleEditImage(selectedImage)}
													className="gap-2"
												>
													<FaEdit/>
													{t('common.edit') || 'Edit'}
												</Button>
											</motion.div>
											
											{/* Download button */}
											<motion.div key="download-button" whileHover={{scale: 1.05}}
														whileTap={{scale: 0.95}}>
												<Button
													onClick={() => handleDownload(selectedImage)}
													className="gap-2"
												>
													<FaDownload/>
													{t('common.download')}
												</Button>
											</motion.div>
											
											{/* Delete button */}
											<motion.div key="delete-button" whileHover={{scale: 1.05}}
														whileTap={{scale: 0.95}}>
												<Button
													variant="destructive"
													onClick={() => handleDelete(selectedImage.id)}
													className="gap-2"
												>
													<FaTrash/>
													{t('common.delete')}
												</Button>
											</motion.div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</motion.div>
			</div>
		);
	};
	
	return (
		<motion.div
			className="container mx-auto px-4 py-8 max-w-6xl"
			initial="hidden"
			animate="visible"
			variants={{
				hidden: {opacity: 0},
				visible: {opacity: 1, transition: {staggerChildren: 0.1}}
			}}
		>
			<motion.div
				className="mb-8 flex items-center justify-between"
				variants={fadeIn}
			>
				<div className="flex items-center gap-3">
					<div className="bg-gradient-to-br from-primary/80 to-primary p-3 rounded-xl shadow-md">
						<FaHistory className="text-white text-xl"/>
					</div>
					<h2 className="text-2xl font-bold text-primary">{t('imageHistory.title')}</h2>
				</div>
				<div className="text-sm text-text-light">
					{t('imageHistory.totalStorage')}: {formatFileSize(totalStorage)}
				</div>
			</motion.div>
			
			<motion.div variants={fadeIn} className="mb-6">
				<Card variant="glass" className="overflow-hidden">
					<CardContent className="p-4">
						<div className="flex flex-col md:flex-row gap-4 items-center justify-between">
							{/* Search */}
							<div className="w-full md:w-1/3">
								<form onSubmit={handleSearch} className="flex items-center">
									<input
										type="text"
										className="w-full p-2 rounded-l-lg border border-background/50 focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none transition-all"
										value={searchQuery}
										onChange={(e) => setSearchQuery(e.target.value)}
										placeholder={t('common.search')}
									/>
									<Button type="submit" variant="default" className="rounded-l-none">
										<FaSearch/>
									</Button>
								</form>
							</div>
							
							{/* Sort */}
							<Button
								variant="outline"
								onClick={handleSortChange}
								className="flex items-center gap-2"
							>
								{sortDirection === 'desc' ? (
									<>
										<FaSortAmountDown key="sort-icon-down" className="text-primary"/>
										<span key="sort-text-newest">{t('common.newest')}</span>
									</>
								) : (
									<>
										<FaSortAmountUp key="sort-icon-up" className="text-primary"/>
										<span key="sort-text-oldest">{t('common.oldest')}</span>
									</>
								)}
							</Button>
						</div>
					</CardContent>
				</Card>
			</motion.div>
			
			{error && (
				<motion.div variants={fadeIn} className="mb-6">
					<Card variant="destructive" className="overflow-hidden">
						<CardContent className="p-4">
							<div className="text-error">{error}</div>
						</CardContent>
					</Card>
				</motion.div>
			)}
			
			<div className="content-area">
				{selectedImage ? (
					renderImageDetails()
				) : (
					<>
						{filteredImages.length === 0 ? (
							<motion.div variants={fadeIn} className="text-center py-12">
								{isLoading ? (
									<Spinner size="lg"/>
								) : (
									<>
										<div className="text-6xl mb-4 opacity-20">🖼️</div>
										<h3 className="text-xl font-medium text-primary mb-2">
											{t('imageHistory.noImagesFound')}
										</h3>
									</>
								)}
							</motion.div>
						) : (
							<>
								<motion.div
									variants={{
										hidden: {opacity: 0},
										visible: {opacity: 1, transition: {staggerChildren: 0.05}}
									}}
									className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
								>
									{filteredImages.map((image, index) => (
										<motion.div key={index} variants={fadeIn}>
											<ImageCard
												image={image}
												onSelect={() => handleImageClick(image)}
												onDownload={() => handleDownload(image)}
												onEdit={() => handleEditImage(image)}
											/>
										</motion.div>
									))}
								</motion.div>
								
								{hasMore && (
									<motion.div variants={fadeIn} className="flex justify-center mt-8">
										<Button
											variant="outline"
											onClick={handleLoadMore}
											disabled={isLoading}
										>
											{isLoading ? <Spinner size="sm" className="mr-2"/> : null}
											{t('common.loadMore')}
										</Button>
									</motion.div>
								)}
							</>
						)}
					</>
				)}
			</div>
		</motion.div>
	);
};

export default ImageHistory;
